import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { assignableRoleSchema, USER_STATUSES, type User } from "@/contracts";

import { usersQuery, type UserFilters } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import { formatRelative } from "@/lib/format";
import { UserEditDialog } from "./user-edit-dialog";
import { UserInviteDialog } from "./user-invite-dialog";
import { ROLE_LABELS, USER_STATUS } from "./user-labels";

/** The whole screen is behind ADMIN and SUPER_ADMIN, in the route and in the API. */
export function UsersPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const filters: UserFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("status") ? { status: filterValue("status") } : {}),
    ...(filterValue("role") ? { role: filterValue("role") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    page,
  };

  const users = useQuery(usersQuery(client, filters));

  const columnCount = isSuperAdmin ? 7 : 6;

  return (
    <>
      <PageHeader
        title={t("usersTitle")}
        description={t("usersDescription")}
        action={<Button onClick={() => setInviting(true)}>{t("inviteMember")}</Button>}
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchUsers")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={tCommon("status")}
              value={filterValue("status")}
              onChange={(value) => setFilter("status", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...USER_STATUSES.map((status) => ({
                  value: status,
                  label: USER_STATUS[status].label,
                })),
              ]}
            />

            {/* Only the assignable roles: the API filters on membership role,
                and no membership carries SUPER_ADMIN. */}
            <SelectField
              label={tCommon("role")}
              value={filterValue("role")}
              onChange={(value) => setFilter("role", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...assignableRoleSchema.options.map((role) => ({
                  value: role,
                  label: ROLE_LABELS[role],
                })),
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {users.isPending ? <LoadingBlock label={t("loadingMembers")} /> : null}

      {users.isError ? (
        <ErrorBlock error={users.error} onRetry={() => void users.refetch()} />
      ) : null}

      {users.data ? (
        <Card>
          <DataTable caption="Organization members">
            <thead>
              <tr>
                <Th>{tCommon("name")}</Th>
                <Th>{tCommon("email")}</Th>
                {isSuperAdmin ? <Th>{tCommon("organization")}</Th> : null}
                <Th>{tCommon("role")}</Th>
                <Th>{tCommon("status")}</Th>
                <Th>{t("colLastSignIn")}</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>

            <tbody>
              {users.data.data.length === 0 ? (
                <EmptyRow colSpan={columnCount}>{t("emptyUsers")}</EmptyRow>
              ) : null}

              {users.data.data.map((member) => {
                // A platform admin is not manageable from inside a tenant
                // (ADR-016), so the button that would always fail is absent.
                const manageable = isSuperAdmin || member.role !== "SUPER_ADMIN";

                return (
                  <tr key={member.id}>
                    <Td className="font-medium">
                      {member.name}
                      {member.id === user?.id ? (
                        <span className="ml-1.5 text-xs font-normal text-ink-muted">(you)</span>
                      ) : null}
                    </Td>
                    <Td className="text-ink-muted">{member.email}</Td>
                    {isSuperAdmin ? <Td>{member.organizationName ?? "No organization"}</Td> : null}
                    <Td>{ROLE_LABELS[member.role]}</Td>
                    <Td>
                      <Badge tone={USER_STATUS[member.status].tone}>
                        {USER_STATUS[member.status].label}
                      </Badge>
                    </Td>
                    <Td>{formatRelative(member.lastLoginAt)}</Td>
                    <Td className="text-right">
                      {manageable ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditing(member)}
                          // Names the row, so a screen reader moving through
                          // the column does not hear "Edit" five times.
                          aria-label={t("editNamed", { name: member.name })}
                        >
                          {t("edit")}
                        </Button>
                      ) : null}
                    </Td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <Td colSpan={columnCount} className="border-b-0">
                  <Pagination
                    page={users.data.pagination.page}
                    totalPages={users.data.pagination.totalPages}
                    total={users.data.pagination.total}
                    noun={{ one: tCommon("noun.member.one"), other: tCommon("noun.member.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <UserInviteDialog open={inviting} onClose={() => setInviting(false)} />

      {/* Mounted per target so the form starts from that member's values
          rather than whoever was edited before. */}
      {editing ? <UserEditDialog user={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}
